import { PROMO_TYPES } from '@exp/components/products/constants';
import { type FacetFilterType } from '@exp/components/filtering/propTypes';

export const CHECKOUT_STEP_CHANGE = 'CHECKOUT_STEP_CHANGE';
export const LOW_STOCK = 'LOW_STOCK';
export const NO_STOCK = 'NO_STOCK';
export const OPEN_EXTERNAL_BROWSER = 'OPEN_EXTERNAL_BROWSER';

// MISC CONSTANTS
export const INTEGER_RADIX = 10;
export const MOBILE_REDIRECT_FLAG = 'ng=true';
export const PRODUCT_SUGGESTIONS_VERSION = 10;

// This needs to mimic what we are using in the old angular app for store-web.
export const NG_STORAGE_GUIDELINES = 'ngStorage-guidelinesRevisionDate';

export const BRAINTREE_PAYMENT_TYPES = {
  APPLE_PAY: 'APPLE_PAY',
  PAYPAL: 'PAYPAL',
  VENMO: 'VENMO',
} as const;
export type BraintreePaymentType = typeof BRAINTREE_PAYMENT_TYPES[keyof typeof BRAINTREE_PAYMENT_TYPES];

// This serves a dual use:
// - CREDIT_CARD and PAYPAL are types returned by pay-config
// - APPLE_PAY, PAYPAL, and VENMO are also subtypes of the pay-config PAYPAL type
/**
 * @deprecated
 * was used to track available payment method types, but its dual purpose makes it awkward to type and use properly
 */
export const PAYMENT_METHOD_TYPES = {
  ...BRAINTREE_PAYMENT_TYPES,
  CREDIT_CARD: 'CREDIT_CARD',
} as const;
export type PaymentMethodType = typeof PAYMENT_METHOD_TYPES[keyof typeof PAYMENT_METHOD_TYPES];

/** root-level payment configuration types */
export const ROOT_PAYMENT_TYPES = {
  CREDIT_CARD: 'CREDIT_CARD',
  /** the braintree type */
  PAYPAL: 'PAYPAL',
} as const;
export type RootPaymentType = typeof ROOT_PAYMENT_TYPES[keyof typeof ROOT_PAYMENT_TYPES];

/** mapping from payment method types to Braintree payload types */
export const BRAINTREE_TOKEN_TYPES = {
  [PAYMENT_METHOD_TYPES.APPLE_PAY]: 'ApplePayCard',
  [PAYMENT_METHOD_TYPES.PAYPAL]: 'PayPalAccount',
  [PAYMENT_METHOD_TYPES.VENMO]: 'VenmoAccount',
} as const;
export type BraintreeTokenType = typeof BRAINTREE_TOKEN_TYPES[keyof typeof BRAINTREE_TOKEN_TYPES];

export const ERROR_REQUIRED = 'ERROR_REQUIRED';
export const ERROR_INCOMPATIBLE = 'ERROR_INCOMPATIBLE';
export const ERROR_INVALID_FORMAT = 'ERROR_INVALID_FORMAT';
export const ERROR_REQUIRE_FUTURE_DATE = 'ERROR_REQUIRE_FUTURE_DATE';
export const ERROR_PO_BOX = 'ERROR_PO_BOX';
export const WARNING_PO_BOX = 'WARNING_PO_BOX';

export const PHONE_PATTERN = /((\(\d{3}\)?)|(\d{3}))([\s-./]?)(\d{3})([\s-./]?)(\d{4})/;
export const FAILABLE_STRING = 'FAIL';
export const PO_BOX_PATTERN = /^ *((#\\d+)|((box|bin)[-. /\\]?\\d+)|(.*p[ .]? ?(o|0)[-. /\\]? *-?((box|bin)|b|(#|num)?\d+))|(p(ost)? *(o(ff(ice)?)?)? *((box|bin)|b)? *\d+)|(p *-?\/?(o)? *-?box)|post office box|((box|bin)|b)( |-)*(number|num|#)? *\d+|(num|number|#) *\d+)/i;
export const US_COUNTRY_ID = 232;
export const US_COUNTRY_CODE = 'US';

export const CARD_DIGIT_PATTERN = /[0-9]{13,16}/;
export const CID_PATTERN = /[0-9]{3,4}/;

export const CREDIT_CARD_TYPES = {
  AMERICAN_EXPRESS: 'AMERICAN_EXPRESS',
  VISA: 'VISA',
  MASTERCARD: 'MASTERCARD',
  DISCOVER: 'DISCOVER',
} as const;
export type CreditCardType = typeof CREDIT_CARD_TYPES[keyof typeof CREDIT_CARD_TYPES];

export const GRANULAR_PAYMENT_TYPES = {
  ...BRAINTREE_PAYMENT_TYPES,
  ...CREDIT_CARD_TYPES,
} as const;
export type GranularPaymentType = typeof GRANULAR_PAYMENT_TYPES[keyof typeof GRANULAR_PAYMENT_TYPES];

export const GRATIS_PAYMENT_TYPE = 'gratis' as const;

export const CARD_TYPE_PREFIX = {
  3: CREDIT_CARD_TYPES.AMERICAN_EXPRESS,
  4: CREDIT_CARD_TYPES.VISA,
  5: CREDIT_CARD_TYPES.MASTERCARD,
  6: CREDIT_CARD_TYPES.DISCOVER,
} as const;

export const ADDRESS_FIELDS = {
  countryCode: {
    name: 'countryCode',
    defaultMessage: 'Country',
    dependentField: 'state',
    required: true,
  },
  firstName: {
    name: 'firstName',
    defaultMessage: 'First Name',
    required: true,
    shippingOnly: false,
  },
  lastName: {
    name: 'lastName',
    defaultMessage: 'Last Name',
    required: true,
    shippingOnly: false,
  },
  address1: {
    name: 'address1',
    billingSize: 30,
    defaultMessage: 'Address 1',
    required: true,
    shippingSize: 55,
  },
  address2: {
    name: 'address2',
    billingSize: 30,
    defaultMessage: 'Apartment, suite, etc. (optional)',
    required: false,
    shippingSize: 55,
  },
  city: {
    name: 'city',
    billingSize: 20,
    defaultMessage: 'City',
    required: true,
    shippingSize: 40,
  },
  state: {
    name: 'state',
    defaultMessage: 'State',
    required: true,
  },
  postalCode: {
    name: 'postalCode',
    billingSize: 10,
    defaultMessage: 'Postal Code',
    required: true,
    shippingSize: 10,
  },
  phone: {
    name: 'phone',
    billingField: 'billingPhone',
    billingSize: 14,
    defaultMessage: 'Phone Number',
    required: true,
    shippingOnly: true,
    shippingSize: 14,
  },
  billingPhone: {
    name: 'billingPhone',
    billingOnly: true,
    billingSize: 14,
    defaultMessage: 'Phone Number',
    required: true,
  },
} as const;
export type AddressField = typeof ADDRESS_FIELDS[keyof typeof ADDRESS_FIELDS];

// Zero padded numbers 01 - 12
export const MONTHS = [...Array(12).keys()].map(i => (i + 1).toString().padStart(2, '0'));
// Now to 20 years from now
export const YEARS = [...Array(20).keys()].map(i => (i + new Date().getFullYear()).toString());

export const PAYMENT_FIELDS = {
  nameOnCard: {
    name: 'nameOnCard',
    defaultMessage: 'Name on Card',
    required: true,
  },
  cardDigits: {
    name: 'cardDigits',
    defaultMessage: 'Card Number',
    dependentFieldReset: 'cardType',
    required: true,
  },
  cardType: {
    name: 'cardType',
    defaultMessage: 'Card Type',
    required: true,
  },
  cardSecurityCode: {
    name: 'cardSecurityCode',
    defaultMessage: 'CVV',
    required: true,
  },
  expirationMonth: {
    name: 'expirationMonth',
    defaultMessage: 'Expiration Month',
    dependentFieldValidate: 'expirationYear',
    required: true,
    selectOptions: MONTHS,
  },
  expirationYear: {
    name: 'expirationYear',
    defaultMessage: 'Year',
    dependentFieldValidate: 'expirationMonth',
    required: true,
    selectOptions: YEARS,
  },
};

// PLP things
export const ACTIVITY_KEY = 'TRAIT.4';
export const ALGOLIA_COLLECTIONS_KEY = 'ALGOLIA_COLLECTION';
export const AVAILABILITY_KEY = 'IN_STOCK_DEAL';
export const BRAND_FILTER_KEY = 'CATEGORY_ROOT';
export const COLLECTION_KEY = 'TRAIT.2';
export const COUPONS_KEY = 'COUPON_ELIGIBLE';
export const DEAL_KEY = 'DEAL';
export const GENDER_KEY = 'TRAIT.3';
export const HOSTED_KEY = 'HOSTED'; // boolean filter key
export const HOSTED_PARAM = 'hosted'; // deal/store id query param
export const ORG_KEY = 'ORGANIZATION';
export const PROMO_KEY = 'TRAIT_PER_DEAL';
export const SUB_BRAND_KEY = 'TRAIT.1';
export const TAXONOMY_KEY = 'TAXONOMY';

export type FilterKey =
  | typeof ACTIVITY_KEY
  | typeof AVAILABILITY_KEY
  | typeof BRAND_FILTER_KEY
  | typeof COLLECTION_KEY
  | typeof COUPONS_KEY
  | typeof DEAL_KEY
  | typeof GENDER_KEY
  | typeof HOSTED_KEY
  | typeof ORG_KEY
  | typeof PROMO_KEY
  | typeof SUB_BRAND_KEY
  | typeof TAXONOMY_KEY;

/** Filter configs specific to store-web */
export type StoreFilterConfig = {
  /** whether the filter values should be parsed as numeric - false */
  numeric?: boolean,
  /** whether the order of the options needs to be preserved in the url - false */
  ordered?: boolean,
  /** the url query param for the filter - null */
  param?: string,
  /** whether to enable the filter appearing on PBP - true */
  pbp?: boolean,
  /** whether to enable the filter appearing on PLP - true */
  plp?: boolean,
  /** used to sort the visible filters - null */
  sortOrder?: number,
};

export type FilterOption = StoreFilterConfig & FacetFilterType;

export const AVAILABLE_FILTERS: Record<FilterKey, FilterOption> = {
  [ACTIVITY_KEY]: {
    defaultMessage: 'Activity',
    filterKey: ACTIVITY_KEY,
    messageKey: 'products.filters.activityLabel',
    multi: true,
    param: 'a',
    sortOrder: 7,
  },
  [AVAILABILITY_KEY]: {
    defaultMessage: 'In Stock',
    filterKey: AVAILABILITY_KEY,
    messageKey: 'products.filters.availabilityLabel',
    multi: false,
    noCount: true,
    param: 'is',
    radio: true,
    sortOrder: 4,
    toggle: true,
  },
  [BRAND_FILTER_KEY]: {
    defaultMessage: 'Brand filters',
    filterKey: BRAND_FILTER_KEY,
    messageKey: 'products.filters.categoriesLabel',
    param: 'bf',
    pbp: false,
    radio: true,
    sortOrder: 3,
  },
  [COLLECTION_KEY]: {
    defaultMessage: 'Collections',
    filterKey: COLLECTION_KEY,
    messageKey: 'products.filters.collectionsLabel',
    multi: true,
    param: 'col',
    pbp: false,
    sortOrder: 5,
  },
  [COUPONS_KEY]: {
    defaultMessage: 'Coupon Available',
    filterKey: COUPONS_KEY,
    messageKey: 'products.filters.couponsLabel',
    multi: false,
    noCount: true,
    param: 'cpn',
    radio: true,
    sortOrder: 9,
    toggle: true,
  },
  [DEAL_KEY]: {
    defaultMessage: 'Deals',
    filterKey: DEAL_KEY,
    messageKey: 'products.filters.dealLabel',
    minCount: 1,
    multi: true,
    numeric: true,
    param: 'd',
    pbp: false,
    sortOrder: 6,
  },
  [GENDER_KEY]: {
    defaultMessage: 'Gender',
    filterKey: GENDER_KEY,
    messageKey: 'products.filters.genderLabel',
    multi: true,
    param: 'g',
    sortOrder: 1,
  },
  [HOSTED_KEY]: {
    defaultMessage: 'Buy On',
    filterKey: HOSTED_KEY,
    messageKey: 'products.filters.hostedLabel',
    noCount: true,
    param: 'h',
    radio: true,
    sortOrder: 11,
  },
  [ORG_KEY]: {
    defaultMessage: 'Brands',
    filterKey: ORG_KEY,
    inlineSearchable: true,
    messageKey: 'products.filters.orgLabel',
    minCount: 1,
    multi: true,
    numeric: true,
    param: 'b',
    plp: false,
    sortOrder: 2,
  },
  [PROMO_KEY]: {
    alphaSort: true,
    defaultMessage: 'Promotions',
    filterKey: PROMO_KEY,
    messageKey: 'products.filters.promoLabel',
    multi: true,
    noCount: true,
    numeric: true,
    param: 'p',
    pbp: true,
    sortOrder: 8,
  },
  [SUB_BRAND_KEY]: {
    defaultMessage: 'Sub-brands',
    filterKey: SUB_BRAND_KEY,
    messageKey: 'products.filters.subBrandsLabel',
    multi: true,
    param: 'sb',
    pbp: false,
    sortOrder: 10,
  },
  [TAXONOMY_KEY]: {
    defaultMessage: 'Categories',
    filterKey: TAXONOMY_KEY,
    messageKey: 'products.filters.taxonomiesLabel',
    numeric: true,
    ordered: true,
    param: 'c',
    sortOrder: 0,
  },
};

export const SORT_TYPE = {
  defaultDirection: 'DESC',
  defaultSort: 'ALGOLIA_PERSONALIZATION',
  values: [
    {
      id: 'ALGOLIA_PERSONALIZATION',
      defaultMessage: 'Relevance',
      direction: 'DESC',
      labelKey: 'products.sort.ALGOLIA_PERSONALIZATION',
      searchOptions: {
        PRSN: null,
      },
      sortOrder: 0,
      type: 'ALGOLIA_PERSONALIZATION',
    },
    {
      id: 'CURATED_POPULARITY_DESC',
      defaultMessage: 'Most Popular',
      direction: 'DESC',
      labelKey: 'products.sort.POPULARITY',
      sortOrder: 2,
      type: 'CURATED_POPULARITY',
    },
    {
      id: 'RECOMMENDED_DESC',
      defaultMessage: 'Average Rating',
      direction: 'DESC',
      labelKey: 'products.sort.RECOMMENDED',
      sortOrder: 3,
      type: 'RECOMMENDED',
    },
    {
      id: 'DESCRIPTION_ASC',
      defaultMessage: 'Alphabetical: A-Z',
      direction: 'ASC',
      labelKey: 'products.sort.DESCRIPTION.ASC',
      sortOrder: 4,
      type: 'DESCRIPTION',
    },
    {
      id: 'DESCRIPTION_DESC',
      defaultMessage: 'Alphabetical: Z-A',
      direction: 'DESC',
      labelKey: 'products.sort.DESCRIPTION.DESC',
      sortOrder: 5,
      type: 'DESCRIPTION',
    },
    {
      id: 'MSRP_DESC',
      defaultMessage: 'Retail Price: High to Low',
      direction: 'DESC',
      labelKey: 'products.sort.MSRP.DESC',
      sortOrder: 6,
      type: 'MSRP',
    },
    {
      id: 'MSRP_ASC',
      defaultMessage: 'Retail Price: Low to High',
      direction: 'ASC',
      labelKey: 'products.sort.MSRP.ASC',
      sortOrder: 7,
      type: 'MSRP',
    },
  ],
};

// Only PLP should show the "Featured" option and default to relevance.
export const PLP_SORT_TYPE = {
  defaultDirection: 'DESC',
  defaultSort: 'ALGOLIA_PERSONALIZATION',
  values: [
    {
      id: 'RELEVANCE_DESC',
      sortOrder: 1,
      direction: 'DESC',
      defaultMessage: 'Featured',
      labelKey: 'products.sort.RELEVANCE',
      type: 'RELEVANCE',
    },
    ...SORT_TYPE.values,
  ],
};

export const ALERT_TYPE = {
  ERROR: { class: 'alert-error', icon: 'warningTriangle', type: 'error' },
  INFO: { class: 'alert-info', icon: 'info', type: 'info' },
  SUCCESS: { class: 'alert-success', icon: 'check-circle', type: 'success' },
  WARNING: { class: 'alert-warning', icon: 'warningTriangle', type: 'warning' },
};

export const DISCOUNT_CODE_STATUS = {
  EXISTING: 'EXISTING', // Previously assigned
  NEW: 'NEW', // Just assigned
  NONE: 'NONE', // Not configured for codes
  UNASSIGNED: 'UNASSIGNED', // Code not yet assigned
  UNAVAILABLE: 'UNAVAILABLE', // Code not available
};

export const DISCOUNT_CODE_SOURCE = {
  EXTERNAL: 'EXTERNAL', // Managed by external integration
  INTERNAL: 'INTERNAL', // Managed internally
};

export const ERROR_CODE = {
  COUPON_INVALID: 'COSXICPN0001',
  COUPON_INELIGIBLE: 'COSXICPN0002',
  PRIZE_INVALID: 'COSXICPN0003',
  COUPON_FAILURE: 'CSRXCPNV0009', // Some call to coupon-xapi or store-services-xapi failed
  COUPON_UNCLAIMED: 'CPNV0005',
  AFFILIATION_REQUIRED: 'SSRXREQD0004',
  CREDENTIAL_REQUIRED: 'SSRXREQD0003',
  EMAIL_REQUIRED: 'SSRXREQD0001',
  EMAIL_REQUIRED_CHECKOUT: 'CSRXREQD0001',
  EMAIL_UNVERIFIED: 'SSRXREQD0002',
  EMAIL_UNVERIFIED_CHECKOUT: 'CSRXREQD0002',
  FRAUDULENT_USER: 'CSRXFRAD0001',
  INVALID_PAYMENT: 'COSXPAYF0001',
  MISSING_SHIPPING_INFO: 'COSXOVLD0003',
  MISSING_SHIPPING_LOCATION: 'COSXCHKO0003',
  NOT_FOUND: 'notFound', // Used by storeWebController when product lookup fails.
  ORDER_RULE_VIOLATION: 'COSXOVLD0001',
  INVALID_SET: 'COSXICTS0002',
  KEY_NOT_FOUND: 'COSXICTS0001',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  UNKNOWN_PRODUCT: 'UNKNOWN_PRODUCT',
  UNAVAILABLE_STORE: 'SSRXDABL0001',
  UPDATE_PAYMENT: 'CSRXPAYF0001',
  PROCESSING_FAILURE: 'SCURPYTC0002',
  PREVIEW_UNTARGETED: 'PREVIEW_UNTARGETED',
} as const;
export type ErrorCode = typeof ERROR_CODE[keyof typeof ERROR_CODE];

// ANALYTIC CONSTANTS
// NOTE: Please keep the wiki up to date:
// https://wiki.experticity.com/display/EN/Current+Analytics+Events#CurrentAnalyticsEvents-StorePages
export const EVENTS = {
  APPLE_PAY_EXPRESS_INVALID_POSTAL_CODE: 'APPLE_PAY_EXPRESS_INVALID_POSTAL_CODE',
  APPLE_PAY_EXPRESS_NO_SHIPPING_METHODS: 'APPLE_PAY_EXPRESS_NO_SHIPPING_METHODS',
  APPLE_PAY_EXPRESS_ORDER_PLACED: 'APPLE_PAY_EXPRESS_ORDER_PLACED',
  APPLE_PAY_EXPRESS_PO_BOX_NOT_ALLOWED: 'APPLE_PAY_EXPRESS_PO_BOX_NOT_ALLOWED',
  APPLE_PAY_EXPRESS_UNKNOWN_ERROR: 'APPLE_PAY_EXPRESS_UNKNOWN_ERROR',
  APPLY_COUPON: 'APPLY_COUPON',
  APPLY_REWARD_CART: 'APPLY_REWARD_CART',
  APPLY_REWARD_CHECKOUT: 'APPLY_REWARD_CHECKOUT',
  CART: 'CART',
  CART_APPLE_PAY_EXPRESS_CLICK: 'CART_APPLE_PAY_EXPRESS_CLICK',
  CART_CHECKOUT_CLICK: 'CART_CHECKOUT_CLICK',
  CART_CONTINUE_SHOPPING: 'CART_CONTINUE_SHOPPING',
  CART_COST_BREAKDOWN_EXPANDED: 'CART_COST_BREAKDOWN_EXPANDED',
  CART_COUPON_CLAIMED: 'CART_COUPON_CLAIMED',
  CART_COUPON_SHOP_CLICK: 'CART_COUPON_SHOP_CLICK',
  CART_COUPON_SHOWN: 'CART_COUPON_SHOWN',
  CART_EMPTY_LINK_CLICK: 'CART_EMPTY_LINK_CLICK',
  CART_ERROR_CLICK: 'CART_ERROR_CLICK',
  CART_FFL_VIEW: 'CART_FFL_VIEW',
  CART_GUIDELINES_CLICK: 'CART_GUIDELINES_CLICK',
  CART_GUIDELINES_CLOSE: 'CART_GUIDELINES_CLOSE',
  CART_HEADER_CLICK: 'CART_HEADER_CLICK',
  CART_GROUP_LOAD: 'CART_GROUP_LOAD',
  CART_GROUPS_LOAD: 'CART_GROUPS_LOAD',
  CART_GROUP_SCROLL: 'CART_GROUP_SCROLL',
  CART_ITEM_CLICK: 'CART_ITEM_CLICK',
  CART_ITEM_DELETE: 'CART_ITEM_DELETE',
  CART_MOVE_TO_SAVE: 'CART_MOVE_TO_SAVE',
  CART_ITEM_QUANTITY_CHANGE: 'CART_ITEM_QUANTITY_CHANGE',
  CART_ORG_SELECTED: 'CART_ORG_SELECTED',
  CART_REACT: 'CART_REACT',
  CART_SET_APPLE_PAY_EXPRESS_CLICK: 'CART_SET_APPLE_PAY_EXPRESS_CLICK',
  CART_SET_CHECKOUT_CLICK: 'CART_SET_CHECKOUT_CLICK',
  CART_SHIPPING_POSTAL_CODE_CLICK: 'CART_SHIPPING_POSTAL_CODE_CLICK',
  CART_TOTALS_CLICK: 'CART_TOTALS_CLICK',
  CART_UNLOCK_CLICK: 'CART_UNLOCK_CLICK',
  CART_VIEW: 'CART_VIEW',
  CART_VIEW_ORDERS_CLICK: 'CART_VIEW_ORDERS_CLICK',
  CATEGORY_LIST: 'CATEGORY_LIST',
  CHECKOUT_ADDRESS_ADD_CLICK: 'CHECKOUT_ADDRESS_ADD_CLICK',
  CHECKOUT_ADDRESS_CONFIRMATION_CLOSE: 'CHECKOUT_ADDRESS_CONFIRMATION_CLOSE',
  CHECKOUT_ADDRESS_CONFIRMATION_CONFIRM: 'CHECKOUT_ADDRESS_CONFIRMATION_CONFIRM',
  CHECKOUT_ADDRESS_CONFIRMATION_EDIT: 'CHECKOUT_ADDRESS_CONFIRMATION_EDIT',
  CHECKOUT_ADDRESS_CONFIRMATION_VIEW: 'CHECKOUT_ADDRESS_CONFIRMATION_VIEW',
  CHECKOUT_ADDRESS_EDIT_CLICK: 'CHECKOUT_ADDRESS_EDIT_CLICK',
  CHECKOUT_ADDRESS_PRIMARY_TOGGLE: 'CHECKOUT_ADDRESS_PRIMARY_TOGGLE',
  CHECKOUT_ADDRESS_REMOVE_CANCEL: 'CHECKOUT_ADDRESS_REMOVE_CANCEL',
  CHECKOUT_ADDRESS_REMOVE_CLICK: 'CHECKOUT_ADDRESS_REMOVE_CLICK',
  CHECKOUT_ADDRESS_REMOVE_CONFIRM: 'CHECKOUT_ADDRESS_REMOVE_CONFIRM',
  CHECKOUT_ADDRESS_SELECT: 'CHECKOUT_ADDRESS_SELECT',
  CHECKOUT_ADDRESS_VALIDATION: 'CHECKOUT_ADDRESS_VALIDATION',
  CHECKOUT_APPLY_COUPON: 'CHECKOUT_APPLY_COUPON',
  CHECKOUT_BACK_CLICK: 'CHECKOUT_BACK_CLICK',
  CHECKOUT_CONFIRM_CLICK: 'CHECKOUT_CONFIRM_CLICK',
  CHECKOUT_CONTINUE_SHOPPING: 'CHECKOUT_CONTINUE_SHOPPING',
  CHECKOUT_EXPIRED_CTA_CLICK: 'CHECKOUT_EXPIRED_CTA_CLICK',
  CHECKOUT_EXPIRED_MODAL_CLOSE: 'CHECKOUT_EXPIRED_MODAL_CLOSE',
  CHECKOUT_EXPIRING_MODAL_CLOSE: 'CHECKOUT_EXPIRING_MODAL_CLOSE',
  CHECKOUT_EXTEND_COUPON_EXPIRATION: 'CHECKOUT_EXTEND_COUPON_EXPIRATION',
  CHECKOUT_GUIDELINES_CLICK: 'CHECKOUT_GUIDELINES_CLICK',
  CHECKOUT_GUIDELINES_PAGE: 'CHECKOUT_GUIDELINES_PAGE',
  CHECKOUT_ORDER_PLACED: 'CHECKOUT_ORDER_PLACED',
  CHECKOUT_ORDER_SET_PLACED: 'CHECKOUT_ORDER_SET_PLACED',
  CHECKOUT_PAYMENT_SAVE_PRIMARY: 'CHECKOUT_PAYMENT_SAVE_PRIMARY',
  CHECKOUT_PAYMENT_SAVE: 'CHECKOUT_PAYMENT_SAVE',
  CHECKOUT_PAYMENT_SUBMIT: 'CHECKOUT_PAYMENT_SUBMIT',
  CHECKOUT_PAYMENT_TYPE_SELECT: 'CHECKOUT_PAYMENT_TYPE_SELECT',
  CHECKOUT_PROCEED_TO_PAYMENT_CLICK: 'CHECKOUT_PROCEED_TO_PAYMENT_CLICK',
  CHECKOUT_REVIEW_EDIT_ORDER: 'CHECKOUT_REVIEW_EDIT_ORDER',
  CHECKOUT_REVIEW_ORDER_CLICK: 'CHECKOUT_REVIEW_ORDER_CLICK',
  CHECKOUT_REVIEW_TOTAL_CLICK: 'CHECKOUT_REVIEW_TOTAL_CLICK',
  CHECKOUT_SAVED_CARD_USED: 'CHECKOUT_SAVED_CARD_USED',
  CHECKOUT_SHIPPING_OPTIONS_VIEW: 'CHECKOUT_SHIPPING_OPTIONS_VIEW',
  CHECKOUT_SHIPPING_SUBMIT: 'CHECKOUT_SHIPPING_SUBMIT',
  CHECKOUT_VALIDATION_ERROR: 'CHECKOUT_VALIDATION_ERROR',
  CHECKOUT_VIEW_ORDERS: 'CHECKOUT_VIEW_ORDERS',
  FAVORITE_ADDED: 'FAVORITE_ADDED',
  FAVORITE_REMOVED: 'FAVORITE_REMOVED',
  FILTER_MENU_INTERACTION: 'FILTER_MENU_INTERACTION',
  ORDER_HEADER_CLICK: 'ORDER_HEADER_CLICK',
  ORDER_UPDATE_PAYMENT: 'ORDER_UPDATE_PAYMENT',
  ORDERHISTORY_CANCEL_CLICK: 'ORDERHISTORY_CANCEL_CLICK',
  ORDERHISTORY_CHANGE_ADDRESS_CLICK: 'ORDERHISTORY_CHANGE_ADDRESS_CLICK',
  ORDERHISTORY_HELP_CLICK: 'ORDERHISTORY_HELP_CLICK',
  ORDERHISTORY_ITEM_CLICK: 'ORDERHISTORY_ITEM_CLICK',
  ORDERHISTORY_LOADMORE: 'ORDERHISTORY_LOADMORE',
  ORDERHISTORY_OPEN_HELP: 'ORDERHISTORY_OPEN_HELP',
  ORDERHISTORY_ORDER_CLICK: 'ORDERHISTORY_ORDER_CLICK',
  ORDERHISTORY_ORG_SELECTED: 'ORDERHISTORY_ORG_SELECTED',
  ORDERHISTORY_REACT: 'ORDERHISTORY_REACT',
  ORDERHISTORY_RECOMMEND_CLICK: 'ORDERHISTORY_RECOMMEND_CLICK',
  ORDERHISTORY_RECOMMEND_EDIT_CLICK: 'ORDERHISTORY_RECOMMEND_EDIT_CLICK',
  ORDERHISTORY_VIEW: 'ORDERHISTORY_VIEW',
  PAYMENT_UPDATE_CONFIRM_CLICK: 'PAYMENT_UPDATE_CONFIRM_CLICK',
  PRODUCT_DETAIL_ACCORDION_CLICK: 'PRODUCT_DETAIL_ACCORDION_CLICK',
  PRODUCT_DETAIL_MENU_CLICK: 'PRODUCT_DETAIL_MENU_CLICK',
  PRODUCT_DETAIL_PAGE: 'PRODUCT_DETAIL_PAGE',
  PRODUCT_LIST: 'PRODUCT_LIST',
  PRODUCT_PREVIEW_MENU_CLICK: 'PRODUCT_PREVIEW_MENU_CLICK',
  PRODUCT_PREVIEW_PAGE: 'PRODUCT_PREVIEW_PAGE',
  PRODUCT_PREVIEW_VIEW: 'PRODUCT_PREVIEW_VIEW',
  PRODUCT_PRODUCT_PAGE: 'PRODUCT_PRODUCT_PAGE',
  PRODUCT_QUESTION_SUBMITTED: 'PRODUCT_QUESTION_SUBMITTED',
  PRODUCT_QUESTION_FEEDBACK: 'PRODUCT_QUESTION_FEEDBACK',
  PRODUCT_QUESTION_SUGGESTION_CLICK: 'PRODUCT_QUESTION_SUGGESTION_CLICK',
  REDIRECT_STORE_BANNER_CTA_CLICK: 'REDIRECT_STORE_BANNER_CTA_CLICK',
  REDIRECT_STORE_DEALS_LOADED: 'REDIRECT_STORE_DEALS_LOADED',
  REMOVE_COUPON: 'REMOVE_COUPON',
  REWARDS_ORDER_CONFIRMATION_PTS_PENDING_VIEWED: 'REWARDS_ORDER_CONFIRMATION_PTS_PENDING_VIEWED',
  REWARDS_PDP_PTS_TOAST_CLICKED: 'REWARDS_PDP_PTS_TOAST_CLICKED',
  REWARDS_PDP_PTS_TOAST_CLOSED: 'REWARDS_PDP_PTS_TOAST_CLOSED',
  REWARDS_PDP_PTS_TOAST_VIEWED: 'REWARDS_PDP_PTS_TOAST_VIEWED',
  SAVED_ITEMS_LOADED: 'SAVED_ITEMS_LOADED',
  SHOW_COUPON_TERMS: 'SHOW_COUPON_TERMS',
  SINGLE_CHECKOUT_ADD_CARD_CLICK: 'SINGLE_CHECKOUT_ADD_CARD_CLICK',
  SINGLE_CHECKOUT_ADDRESS_SUGGESTION_SELECTED: 'SINGLE_CHECKOUT_ADDRESS_SUGGESTION_SELECTED',
  SINGLE_CHECKOUT_ADDRESS_SUGGESTIONS_SHOWN: 'SINGLE_CHECKOUT_ADDRESS_SUGGESTIONS_SHOWN',
  SINGLE_CHECKOUT_BACK_CLICK: 'SINGLE_CHECKOUT_BACK_CLICK',
  SINGLE_CHECKOUT_BILLING_USE_SHIPPING_ADDRESS_TOGGLE: 'SINGLE_CHECKOUT_BILLING_USE_SHIPPING_ADDRESS_TOGGLE',
  SINGLE_CHECKOUT_BRAINTREE_BUTTON_CLICK: 'SINGLE_CHECKOUT_BRAINTREE_BUTTON_CLICK',
  SINGLE_CHECKOUT_CANCEL_REMOVE_SAVED_CARD: 'SINGLE_CHECKOUT_CANCEL_REMOVE_SAVED_CARD',
  SINGLE_CHECKOUT_CHANGE_ADDRESS_CLICK: 'SINGLE_CHECKOUT_CHANGE_ADDRESS_CLICK',
  SINGLE_CHECKOUT_CHANGE_PAYMENT_METHOD_CLICK: 'SINGLE_CHECKOUT_CHANGE_PAYMENT_METHOD_CLICK',
  SINGLE_CHECKOUT_CONFIRM_REMOVE_SAVED_CARD: 'SINGLE_CHECKOUT_CONFIRM_REMOVE_SAVED_CARD',
  SINGLE_CHECKOUT_CONTINUE_SHOPPING_CLICK: 'SINGLE_CHECKOUT_CONTINUE_SHOPPING_CLICK',
  SINGLE_CHECKOUT_CREDIT_CARD_CTA_CLICK: 'SINGLE_CHECKOUT_CREDIT_CARD_CTA_CLICK',
  SINGLE_CHECKOUT_EDIT_SAVED_CARD_CLICK: 'SINGLE_CHECKOUT_EDIT_SAVED_CARD_CLICK',
  SINGLE_CHECKOUT_ERROR: 'SINGLE_CHECKOUT_ERROR',
  SINGLE_CHECKOUT_HEADER_LOGO_CLICK: 'HEADER_LOGO',
  SINGLE_CHECKOUT_REMOVE_SAVED_CARD_CLICK: 'SINGLE_CHECKOUT_REMOVE_SAVED_CARD_CLICK',
  SINGLE_CHECKOUT_SELECT_SAVED_CARD: 'SINGLE_CHECKOUT_SELECT_SAVED_CARD',
  SINGLE_CHECKOUT_SHIPPING_CHANGE: 'SINGLE_CHECKOUT_SHIPPING_CHANGE',
  SINGLE_CHECKOUT_SHIPPING_CTA_CLICK: 'SINGLE_CHECKOUT_SHIPPING_CTA_CLICK',
  SINGLE_CHECKOUT_SHIPPING_OTHER_OPTIONS_CLICK: 'SINGLE_CHECKOUT_SHIPPING_OTHER_OPTIONS_CLICK',
  SINGLE_CHECKOUT_SUMMARY_FEES_TOGGLE: 'SINGLE_CHECKOUT_SUMMARY_FEES_TOGGLE',
  SINGLE_CHECKOUT_SUMMARY_SHIPPING_TOGGLE: 'SINGLE_CHECKOUT_SUMMARY_SHIPPING_TOGGLE',
  START_RETURN_CLICK: 'START_RETURN_CLICK',
  STORE_BANNER_CLICK: 'STORE_BANNER_CLICK',
  STORE_BANNER_EXTRA_DETAILS_CLICK: 'STORE_BANNER_EXTRA_DETAILS_CLICK',
  STORE_BANNER_SEE_ELIGIBLE_PRODUCTS_CLICK: 'STORE_BANNER_SEE_ELIGIBLE_PRODUCTS_CLICK',
  STORE_CATEGORY_CARD_CLICK: 'STORE_CATEGORY_CARD_CLICK',
  STORE_CATEGORY_LIST_VIEW: 'STORE_CATEGORY_LIST_VIEW',
  STORE_DEAL_SELECTOR_DEAL_CLICK: 'STORE_DEAL_SELECTOR_DEAL_CLICK',
  STORE_DEAL_SELECTOR_MODAL_CLOSE: 'STORE_DEAL_SELECTOR_MODAL_CLOSE',
  STORE_DEAL_SELECTOR_MODAL_VIEW: 'STORE_DEAL_SELECTOR_MODAL_VIEW',
  STORE_DEAL_SELECTOR_UNLOCK_CLICK: 'STORE_DEAL_SELECTOR_UNLOCK_CLICK',
  STORE_DEAL_HOSTED_CLICK: 'STORE_DEAL_HOSTED_CLICK',
  STORE_EMAIL_RESEND_CLICK: 'STORE_EMAIL_RESEND_CLICK',
  STORE_ERROR: 'STORE_ERROR',
  STORE_ERROR_ADD_EMAIL_CLICK: 'STORE_ERROR_ADD_EMAIL_CLICK',
  STORE_ERROR_HELP_DESK_CLICK: 'STORE_ERROR_HELP_DESK_CLICK',
  STORE_ERROR_VIEW: 'STORE_ERROR_VIEW',
  STORE_GUIDELINES_CLICK: 'STORE_GUIDELINES_CLICK',
  STORE_GUIDELINES_SHOWED: 'STORE_GUIDELINES_SHOWED',
  STORE_NAV_CLICK: 'STORE_NAV_CLICK',
  STORE_NAV_IN_STOCK_ONLY: 'STORE_NAV_IN_STOCK_ONLY',
  STORE_NAV_SORTORDER_OPEN: 'STORE_NAV_SORTORDER_OPEN',
  STORE_NAV_SORTORDER_SELECTED: 'STORE_NAV_SORTORDER_SELECTED',
  STORE_PAGINATION_CLICK: 'STORE_PAGINATION_CLICK',
  STORE_PRODUCT_DETAIL_COUPON_CLAIMED: 'STORE_PRODUCT_DETAIL_COUPON_CLAIMED',
  STORE_PRODUCT_DETAIL_COUPON_SHOWN: 'STORE_PRODUCT_DETAIL_COUPON_SHOWN',
  STORE_PRODUCT_DETAIL_DEAL_SELECTOR_CLOSE: 'STORE_PRODUCT_DETAIL_DEAL_SELECTOR_CLOSE',
  STORE_PRODUCT_DETAIL_DEAL_SELECTOR_CURRENT_STORE_CLICK: 'STORE_PRODUCT_DETAIL_DEAL_SELECTOR_CURRENT_STORE_CLICK',
  STORE_PRODUCT_DETAIL_DEAL_SELECTOR_EXPANDED: 'STORE_PRODUCT_DETAIL_DEAL_SELECTOR_EXPANDED',
  STORE_PRODUCT_DETAIL_DEAL_SELECTOR_GUIDELINES_CLOSE: 'STORE_PRODUCT_DETAIL_DEAL_SELECTOR_GUIDELINES_CLOSE',
  STORE_PRODUCT_DETAIL_DEAL_SELECTOR_GUIDELINES_VIEW: 'STORE_PRODUCT_DETAIL_DEAL_SELECTOR_GUIDELINES_VIEW',
  STORE_PRODUCT_DETAIL_DEAL_SELECTOR_STORE_CLICK: 'STORE_PRODUCT_DETAIL_DEAL_SELECTOR_STORE_CLICK',
  STORE_PRODUCT_DETAIL_DEAL_SELECTOR_VIEW: 'STORE_PRODUCT_DETAIL_DEAL_SELECTOR_VIEW',
  STORE_PRODUCT_DETAIL_LOW_STOCK_NOTIFICATION: 'STORE_PRODUCT_DETAIL_LOW_STOCK_NOTIFICATION',
  STORE_PRODUCT_DETAIL_MISCONFIGURED_PRODUCT: 'STORE_PRODUCT_DETAIL_MISCONFIGURED_PRODUCT',
  STORE_PRODUCT_DETAIL_NO_OPTION_VALUES: 'STORE_PRODUCT_DETAIL_NO_OPTION_VALUES',
  STORE_PRODUCT_DETAIL_RESTRICTED_LINK_CLICK: 'STORE_PRODUCT_DETAIL_RESTRICTED_LINK_CLICK',
  STORE_PRODUCT_DETAIL_START_LEARNING_CLICK: 'STORE_PRODUCT_DETAIL_START_LEARNING_CLICK',
  STORE_PRODUCT_DETAIL_THE_DROP_VIEW: 'STORE_PRODUCT_DETAIL_THE_DROP_VIEW',
  STORE_PRODUCT_DETAILS_ADD_TO_CART: 'STORE_PRODUCT_DETAILS_ADD_TO_CART',
  STORE_PRODUCT_DETAILS_ADD_TO_CART_DISABLED: 'STORE_PRODUCT_DETAILS_ADD_TO_CART_DISABLED',
  STORE_PRODUCT_DETAILS_AFFILIATE_DETAILS_CLICK: 'STORE_PRODUCT_DETAILS_AFFILIATE_DETAILS_CLICK',
  STORE_PRODUCT_DETAILS_AFFILIATE_LINK_CLICK: 'STORE_PRODUCT_DETAILS_AFFILIATE_LINK_CLICK',
  STORE_PRODUCT_DETAILS_AFFILIATE_SETTINGS_LINK_CLICK: 'STORE_PRODUCT_DETAILS_AFFILIATE_SETTINGS_LINK_CLICK',
  STORE_PRODUCT_DETAILS_IMAGE_BROKEN: 'STORE_PRODUCT_DETAILS_IMAGE_BROKEN',
  STORE_PRODUCT_DETAILS_IMAGE_CLICK: 'STORE_PRODUCT_DETAILS_IMAGE_CLICK',
  STORE_PRODUCT_DETAILS_MODULE_CLICK: 'STORE_PRODUCT_DETAILS_MODULE_CLICK',
  STORE_PRODUCT_DETAILS_MODULE_VIEW: 'STORE_PRODUCT_DETAILS_MODULE_VIEW',
  STORE_PRODUCT_DETAILS_NO_STOCK_NOTIFY: 'STORE_PRODUCT_DETAILS_NO_STOCK_NOTIFY',
  STORE_PRODUCT_DETAILS_OPTION_CLICK: 'STORE_PRODUCT_DETAILS_OPTION_CLICK',
  STORE_PRODUCT_DETAILS_OPTION_SELECT: 'STORE_PRODUCT_DETAILS_OPTION_SELECT',
  STORE_PRODUCT_DETAILS_QUANTITY_CHANGE: 'STORE_PRODUCT_DETAILS_QUANTITY_CHANGE',
  STORE_PRODUCT_DETAILS_RECOMMENDATIONS_SUMMARY_CLICK: 'STORE_PRODUCT_DETAIL_RECOMMENDATIONS_SUMMARY_CLICK',
  STORE_PRODUCT_DETAILS_RECOMMENDATIONS_SUMMARY_FETCHED: 'STORE_PRODUCT_DETAIL_RECOMMENDATIONS_SUMMARY_FETCHED',
  STORE_PRODUCT_DETAILS_SIGN_IN: 'STORE_PRODUCT_DETAILS_SIGN_IN',
  STORE_PRODUCT_DETAILS_SIZE_GUIDE_CLICK: 'STORE_PRODUCT_DETAILS_SIZE_GUIDE_CLICK',
  STORE_PRODUCT_DETAILS_SIZE_GUIDE_CLOSE: 'STORE_PRODUCT_DETAILS_SIZE_GUIDE_CLOSE',
  STORE_PRODUCT_DETAILS_SUBMIT: 'STORE_PRODUCT_DETAILS_SUBMIT',
  STORE_PRODUCT_DETAILS_TAB_CLICK: 'STORE_PRODUCT_DETAILS_TAB_CLICK',
  STORE_PRODUCT_DETAILS_UNAVAILABLE_COMBINATION_REQUEST: 'STORE_PRODUCT_DETAILS_UNAVAILABLE_COMBINATION_REQUEST',
  STORE_PRODUCT_DETAILS_UNLOCK_DISCOUNT_CLICK: 'STORE_PRODUCT_DETAILS_UNLOCK_DISCOUNT_CLICK',
  STORE_PRODUCT_DETAILS_VIEW: 'STORE_PRODUCT_DETAILS_VIEW',
  STORE_PRODUCT_LIST_COUPON_SHOP_CLICK: 'STORE_PRODUCT_LIST_COUPON_SHOP_CLICK',
  STORE_PRODUCT_LIST_COUPON_SHOWN: 'STORE_PRODUCT_LIST_COUPON_SHOWN',
  STORE_PRODUCT_LIST_LESSONS_LINK_CLICK: 'STORE_PRODUCT_LIST_LESSONS_LINK_CLICK',
  STORE_PRODUCT_LIST_VIEW: 'STORE_PRODUCT_LIST_VIEW',
  STORE_PRODUCT_SEARCH_BOX_CLICK: 'STORE_PRODUCT_SEARCH_BOX_CLICK',
  STORE_PRODUCT_SEARCH_CLEAR_RESULTS: 'STORE_PRODUCT_SEARCH_CLEAR_RESULTS',
  STORE_PRODUCT_SEARCH_EXECUTED_ENTER_PRESS: 'STORE_PRODUCT_SEARCH_EXECUTED_ENTER_PRESS',
  STORE_PRODUCT_SEARCH_EXECUTED_ICON_CLICK: 'STORE_PRODUCT_SEARCH_EXECUTED_ICON_CLICK',
  STORE_PRODUCT_SUGGESTION_LOAD: 'STORE_PRODUCT_SUGGESTION_LOAD',
  STORE_PUCHOUT_CODE_CLICK: 'STORE_PUCHOUT_CODE_CLICK',
  STORE_PUNCHOUT_MODAL_CLOSE: 'STORE_PUNCHOUT_MODAL_CLOSE',
  STORE_PUNCHOUT_MODAL_START_LEARNING_CLICK: 'STORE_PUNCHOUT_MODAL_START_LEARNING_CLICK',
  STORE_PUNCHOUT_MODAL_VIEW: 'STORE_PUNCHOUT_MODAL_VIEW',
  STORE_PUNCHOUT_REDEEM_CLICK: 'STORE_PUNCHOUT_REDEEM_CLICK',
  STORE_PUNCHOUT_SUBMIT: 'STORE_PUNCHOUT_SUBMIT',
  STORE_PUNCHOUT_VIEW: 'STORE_PUNCHOUT_VIEW',
  STORE_PUNCHOUT_VISIT_BRAND_TOOLTIP: 'STORE_PUNCHOUT_VISIT_BRAND_TOOLTIP',
  STORE_PUNCHOUT_VISIT_BRAND: 'STORE_PUNCHOUT_VISIT_BRAND',
  STORE_SEARCH_ALL_BRANDS_CLICK: 'STORE_SEARCH_ALL_BRANDS_CLICK',
  STORE_SEARCH_NO_RESULTS_VIEW: 'STORE_SEARCH_NO_RESULTS_VIEW',
  STORE_SELECTOR_CLOSE: 'STORE_SELECTOR_CLOSE',
  STORE_SELECTOR_ERROR_VIEW: 'STORE_SELECTOR_ERROR_VIEW',
  STORE_SELECTOR_GUIDELINES_CLOSE: 'STORE_SELECTOR_GUIDELINES_CLOSE',
  STORE_SELECTOR_GUIDELINES_VIEW: 'STORE_SELECTOR_GUIDELINES_VIEW',
  STORE_SELECTOR_STORE_CLICK: 'STORE_SELECTOR_STORE_CLICK',
  STORE_SELECTOR_VIEW: 'STORE_SELECTOR_VIEW',
  STORE_SORT_CLICK: 'STORE_SORT_CLICK',
  STORE_AVAILABILITY_REQUEST_CLICK: 'STORE_AVAILABILITY_REQUEST_CLICK',
  STORE_VIEW_ALL_BRAND_PRODUCTS_CLICK: 'STORE_VIEW_ALL_BRAND_PRODUCTS_CLICK',
  TOP_PRODUCTS_CHANGE_TAXONOMY: 'TOP_PRODUCTS_CHANGE_TAXONOMY',
  TOP_PRODUCTS_PROFILE_CLICK: 'TOP_PRODUCTS_PROFILE_CLICK',
  TOP_PRODUCTS_AMAZON_CLICK: 'TOP_PRODUCTS_AMAZON_CLICK',
  TOP_PRODUCTS: 'TOP_PRODUCTS',
} as const;

export const PRODUCT_CARD_SOURCES = {
  CART_SUGGESTIONS: 'CART_SUGGESTIONS',
  PDP_SUGGESTIONS: 'PDP_SUGGESTIONS',
  PRODUCT_LIST: 'PRODUCT_LIST',
};

export const PRIZE_QUERY_PARAM = 'prizeId';

export const PRIZE_STATUS = {
  AVAILABLE: 'AVAILABLE',
  PENDING: 'PENDING',
} as const;
export type PrizeStatus = typeof PRIZE_STATUS[keyof typeof PRIZE_STATUS];

export const PDP_QUERY_PARAMS = {
  AUTOLAUNCH: 'launch',
  DEAL: 'd',
  PARTITION: 'section',
  PRERATED: 'score',
  PRESTARRATED: 'stars',
  PRODUCT_CODE: 'p',
  RECOMMENDATIONS_PARTITION: 'recommendations',
  RECOMMENDATION: 'recommendation',
};

export const PROMO_VALUES = {
  [PROMO_TYPES.EXTRA_SAVINGS]: 5,
  [PROMO_TYPES.FREE_SHIPPING]: 6,
  [PROMO_TYPES.OUTLET]: 7,
  [PROMO_TYPES.FLASH_DEAL]: 8,
  [PROMO_TYPES.GIFTABLE]: 9,
  [PROMO_TYPES.DAILY_LIMITED_TIME_DEAL]: 12,
  REWARDS_ELIGIBLE: 15,
};

// Alternate modes for the PLP that have implied settings, filters, and banners
export const PLP_ALT_MODES = {
  [PROMO_TYPES.OUTLET]: {
    commonName: {
      defaultMessage: 'Outlet',
      messageKey: 'common.outlet',
    },
    facets: {
      [PROMO_KEY]: [PROMO_VALUES.OUTLET],
    },
    feedPageUrl: '/home/page/outlet',
    key: PROMO_TYPES.OUTLET,
  },
};

export const BREADCRUMB_NODE_IDS = {
  ALL: 'ALL',
  BRAND: 'BRAND',
};

export const TEST_KEYS = {
  PDP_QUESTIONS: 'PDP_QUESTIONS',
  PDP_SUGGESTIONS_VERSION: 'PDP_SUGGESTIONS_VERSION', // manually set the suggestions api version
  PUNCHOUT_INSTRUCTIONS: 'PUNCHOUT_INSTRUCTIONS',
};

export const GUIDELINES_PARTS = {
  GENERAL: 'general',
  PURCHASING: 'purchasing',
  RETURNS: 'returns',
  SHIPPING: 'shipping',
  WARRANTY: 'warranty',
} as const;
export type GuidelinesPart = typeof GUIDELINES_PARTS[keyof typeof GUIDELINES_PARTS];

export const CHECKOUT_STEP_ID = {
  confirm: 'confirm',
  payment: 'payment',
  review: 'review',
  shipping: 'shipping',
};

export const SHIPPING_RESTRICTION_TYPE = {
  DEALER: 'DEALER',
  /** @deprecated */
  LOCATION: 'LOCATION',
  PERSONAL: 'PERSONAL',
} as const;
export type ShippingRestrictionType = typeof SHIPPING_RESTRICTION_TYPE[keyof typeof SHIPPING_RESTRICTION_TYPE];

export const SEARCH_PROVIDERS = {
  MODULE: 'module',
};
